import axios from 'axios';
import { STORAGE_KEYS, API_ENDPOINTS } from '../utils/constants';
import { getStorageItem, setStorageItem, removeStorageItem } from '../utils/helpers';

/**
 * Token refresh service
 * Handles automatic token refresh with HttpOnly cookies
 *
 * NOTA: Con HttpOnly cookies, los tokens se gestionan automáticamente por el navegador.
 * Este servicio ya no necesita guardar/leer tokens del localStorage.
 */
class TokenService {
  constructor() {
    this.isRefreshing = false;
    this.failedQueue = [];
  }

  /**
   * Process queued requests after token refresh
   */
  processQueue(error, success = false) {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(success);
      }
    });

    this.failedQueue = [];
  }

  /**
   * Get stored access token
   * DEPRECADO: Con HttpOnly cookies, el frontend no puede acceder a los tokens.
   * Los tokens se envían automáticamente en cada request.
   */
  getAccessToken() {
    // Los tokens ahora están en HttpOnly cookies, no son accesibles desde JS
    // Mantenemos esta función por retrocompatibilidad, pero devuelve null
    return null;
  }

  /**
   * Get stored refresh token
   * DEPRECADO: Con HttpOnly cookies, el frontend no puede acceder a los tokens.
   */
  getRefreshToken() {
    // Los tokens ahora están en HttpOnly cookies, no son accesibles desde JS
    return null;
  }

  /**
   * Store tokens
   * DEPRECADO: Los tokens ahora se gestionan automáticamente por cookies HttpOnly
   */
  setTokens(accessToken, refreshToken) {
    // No hacemos nada, los tokens están en cookies HttpOnly gestionadas por el navegador
    // Las cookies se setean automáticamente cuando el backend las envía
  }

  /**
   * Clear all tokens
   * Ahora solo limpia los datos de usuario del localStorage
   */
  clearTokens() {
    // Con HttpOnly cookies, no podemos eliminar las cookies desde JS
    // Las cookies se limpian llamando al endpoint /logout del backend
    removeStorageItem(STORAGE_KEYS.USER);

    // Si había tokens antiguos en localStorage (migración), los limpiamos
    removeStorageItem(STORAGE_KEYS.TOKEN);
    removeStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Refresh access token using refresh token from HttpOnly cookies
   */
  async refreshAccessToken() {
    try {
      // Con HttpOnly cookies, el refresh_token se envía automáticamente
      // No necesitamos enviarlo en el body
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL || 'https://api.teonila.com'}${API_ENDPOINTS.AUTH.REFRESH}`,
        {}, // Body vacío, el backend lee el refresh_token desde cookies
        {
          withCredentials: true,  // Importante: incluir cookies
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Las nuevas cookies se setean automáticamente por el navegador
      // No necesitamos hacer nada más
      return true;
    } catch (error) {
      // If refresh fails, clear all auth data
      this.clearTokens();
      throw error;
    }
  }

  /**
   * Handle token refresh with queue for concurrent requests
   * Con HttpOnly cookies, solo necesitamos refrescar y reintentar el request
   */
  async handleTokenRefresh(originalRequest) {
    if (this.isRefreshing) {
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      }).then(() => {
        // Asegurarse de que withCredentials esté configurado en el retry
        originalRequest.withCredentials = true;
        return axios(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      });
    }

    this.isRefreshing = true;

    try {
      await this.refreshAccessToken();
      this.processQueue(null, true);
      // Asegurarse de que withCredentials esté configurado en el retry
      originalRequest.withCredentials = true;
      return axios(originalRequest);
    } catch (error) {
      this.processQueue(error, false);

      // Redirect to login
      window.location.href = '/login';
      return Promise.reject(error);
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Revoke refresh token (for logout from specific device)
   * DEPRECADO: Ahora usar el endpoint /logout en su lugar
   */
  async revokeRefreshToken(token = null) {
    // Con HttpOnly cookies, no podemos acceder al refresh_token desde JS
    // El backend lo lee desde las cookies automáticamente
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL || 'https://api.teonila.com'}${API_ENDPOINTS.AUTH.REVOKE_REFRESH}`,
        {}, // Body vacío, el backend lee desde cookies
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Failed to revoke refresh token:', error);
    }
  }

  /**
   * Revoke all refresh tokens (logout from all devices)
   */
  async revokeAllRefreshTokens() {
    // Con HttpOnly cookies, el access_token se envía automáticamente
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL || 'https://api.teonila.com'}${API_ENDPOINTS.AUTH.REVOKE_ALL_REFRESH}`,
        {},
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Failed to revoke all refresh tokens:', error);
    }
  }

  /**
   * Check if access token is expired or about to expire
   * DEPRECADO: Con HttpOnly cookies, no podemos acceder al token para verificar su expiración
   * El backend maneja esto automáticamente y devuelve 401 cuando el token expira
   */
  isTokenExpired(bufferSeconds = 30) {
    // Con HttpOnly cookies, no podemos leer el token para verificar su expiración
    // El backend maneja esto automáticamente
    // Siempre devolvemos false porque no podemos verificar
    return false;
  }

  /**
   * Proactively refresh token if it's about to expire
   * DEPRECADO: Con HttpOnly cookies, no podemos verificar la expiración proactivamente
   * El interceptor de axios maneja el refresh automáticamente cuando recibe un 401
   */
  async refreshIfNeeded() {
    // Con HttpOnly cookies, no podemos verificar la expiración del token
    // El refresh se maneja automáticamente en el interceptor cuando recibimos un 401
    return true;
  }
}

// Export singleton instance
export default new TokenService();